/**
 * KPI tiles: headline number, delta vs prior (coloured by `good`, never by sign, always with an arrow and words), target
 * (Reports → Settings) with met / missed in words, and sample, or the missing state in words. A metric the viewer may
 * not see is not rendered at all.
 * Owner: slice web-charts (#1115).
 */
import { cn } from '@patchbay/ui'
import { ArrowDown, ArrowUp, CircleCheck, CircleX, Minus } from 'lucide-react'
import type { Drill, Metric } from '../../../lib/api/generated'
import {
  computeDelta,
  formatCount,
  formatValue,
  isVisible,
  metricNumber,
  metricPrior,
  slotStripped,
  toNum,
  type Visibility,
} from './format'
import { StateText } from './state-text'

export type OnDrill = (drill: Drill) => void

export function metricVisible(metric: Metric, canSee?: (v: Visibility) => boolean): boolean {
  const ok = (metric.state ?? 'ok') === 'ok'
  return isVisible(metric.visibility, canSee, ok && slotStripped(metric, metric.visibility))
}

const TONE = {
  good: 'text-success',
  bad: 'text-destructive',
  neutral: 'text-muted-foreground',
} as const

export function DeltaBadge({ metric }: { metric: Metric }) {
  const d = computeDelta(metric.unit, metricNumber(metric), metricPrior(metric), metric.good ?? 'neutral')
  if (!d) return null
  const Icon = d.direction === 'up' ? ArrowUp : d.direction === 'down' ? ArrowDown : Minus
  const words = d.direction === 'flat' ? 'No change vs prior period' : `${d.direction === 'up' ? 'Up' : 'Down'} ${d.text} vs prior period`
  return (
    <span
      data-delta={d.direction}
      data-tone={d.tone}
      className={cn('inline-flex items-center gap-0.5 text-xs font-medium', TONE[d.tone])}
      title={words}
    >
      <Icon aria-hidden className="size-3.5" />
      <span className="tabular">{d.direction === 'flat' ? 'no change' : d.text}</span>
      <span className="sr-only">{words}</span>
    </span>
  )
}

export interface TargetStatus {
  /** "Target ≤ 30 min" / "Target ≥ 90.0%". */
  line: string
  /** null when there is no value to judge (missing, insufficient) or the metric has no good direction. */
  met: boolean | null
  /** The whole thing in words, for aria and the tooltip. */
  words: string
}

/** The tile's target line and met / missed, judged with `metric.good` (lower is better for `down`). */
export function targetStatus(metric: Metric): TargetStatus | null {
  const target = toNum(metric.target)
  if (target === null) return null
  const good = metric.good ?? 'neutral'
  const formatted = formatValue(metric.unit, target)
  const line = `Target ${good === 'down' ? '≤ ' : good === 'up' ? '≥ ' : ''}${formatted}`
  const value = (metric.state ?? 'ok') === 'ok' ? toNum(metricNumber(metric)) : null
  const met = value === null || good === 'neutral' ? null : good === 'down' ? value <= target : value >= target
  const goal = `Target ${good === 'down' ? 'at most ' : good === 'up' ? 'at least ' : ''}${formatted}`
  return { line, met, words: met === null ? goal : `${goal}: ${met ? 'met' : 'missed'}` }
}

export function TargetBadge({ status }: { status: TargetStatus }) {
  if (status.met === null) return null
  const Icon = status.met ? CircleCheck : CircleX
  return (
    <span
      data-target-status={status.met ? 'met' : 'missed'}
      aria-hidden
      className={cn(
        'inline-flex items-center gap-0.5 whitespace-nowrap text-xs font-medium',
        status.met ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon aria-hidden className="size-3.5" />
      {status.met ? 'Met' : 'Missed'}
    </span>
  )
}

export function KpiTile({ metric, onDrill, canSee }: { metric: Metric; onDrill?: OnDrill; canSee?: (v: Visibility) => boolean }) {
  if (!metricVisible(metric, canSee)) return null
  const state = metric.state ?? 'ok'
  const raw = metricNumber(metric)
  const ok = state === 'ok' && raw !== null
  const drill = metric.drill
  const clickable = ok && !!drill && !!onDrill
  const target = targetStatus(metric)
  const sample = ok && metric.sample != null ? `n=${formatCount(metric.sample)}` : null
  const body = (
    <>
      <span className="text-xs font-medium break-words text-muted-foreground">{metric.label}</span>
      {ok ? (
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span data-kpi-value className="tabular text-2xl font-semibold text-foreground">
            {formatValue(metric.unit, raw)}
          </span>
          <DeltaBadge metric={metric} />
        </span>
      ) : state === 'ok' ? (
        // An ok metric with no value carries no sample reason: say "No data", never "Too few cases".
        <span data-state="no_data" className="text-sm font-medium text-muted-foreground">
          No data
        </span>
      ) : (
        <StateText state={state} note={metric.note} sample={metric.sample} />
      )}
      {target || sample ? (
        <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          {target ? (
            <>
              <span data-target className="tabular whitespace-nowrap" aria-hidden>
                {target.line}
              </span>
              <span className="sr-only">{target.words}</span>
            </>
          ) : null}
          {target ? <TargetBadge status={target} /> : null}
          {target && sample ? <span aria-hidden>·</span> : null}
          {sample ? <span className="tabular">{sample}</span> : null}
        </span>
      ) : null}
      {ok && metric.note ? <span className="text-xs text-muted-foreground">{metric.note}</span> : null}
    </>
  )
  const cls = 'reports-root flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-card p-3 text-left'
  if (clickable) {
    return (
      <button
        type="button"
        data-kpi={metric.key}
        className={cn(cls, 'cursor-pointer transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring')}
        onClick={() => drill && onDrill?.(drill)}
        title={target?.words}
        aria-label={`${metric.label}: ${formatValue(metric.unit, raw)}.${target ? ` ${target.words}.` : ''} Open the cases behind it`}
      >
        {body}
      </button>
    )
  }
  return (
    <div data-kpi={metric.key} className={cls} title={target?.words}>
      {body}
    </div>
  )
}

/** A row of tiles; wraps 2-up on a phone (an odd last tile takes the full row). Hidden metrics leave no hole. */
export function KpiStrip({ metrics, onDrill, canSee }: { metrics: Metric[]; onDrill?: OnDrill; canSee?: (v: Visibility) => boolean }) {
  const shown = metrics.filter((m) => metricVisible(m, canSee))
  return (
    <div
      className="reports-root grid grid-cols-2 gap-3 max-sm:[&>*:last-child:nth-child(odd)]:col-span-2 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]"
      data-kpi-strip
    >
      {shown.map((m) => (
        <KpiTile key={m.key} metric={m} onDrill={onDrill} canSee={canSee} />
      ))}
    </div>
  )
}
